/**
 * Robot-config slice — ingests DataChannel `robot-config` / `capabilities-error`
 * messages into a catalog + limits snapshot for the control instance.
 *
 * Framework-free. Owns robot-config state, the camera catalog (a sub-slice) and
 * capabilities-request scheduling.
 */

import { normalizeCamerasPayload } from "./camera-catalog";
import type { CameraCatalogEntry } from "./camera-catalog";
import { resolveCommandCatalog } from "./command-catalog";
import type { CommandCatalog, RobotCommandEntry } from "./command-catalog";
import { createStore } from "./store";
import type { StoreApi } from "./store";

export type RobotConfigStatus = "pending" | "ready" | "error";

export interface RobotLimits {
  max_vx?: number;
  max_vy?: number;
  max_vyaw?: number;
  /** Ackermann/rover ceiling — present instead of max_vy/max_vyaw, never alongside
   * them (see command-catalog.ts's resolveProfile). */
  max_steer_angle?: number;
}

/**
 * True when `limits` describes an Ackermann/rover robot (a genuinely usable
 * max_steer_angle ceiling), false for holonomic or unknown. The single source of
 * truth for this check outside command-catalog.ts's own (stricter, mutual-exclusion-
 * checking) resolveProfile — speed-limits.ts and any UI deriving "which axis set does
 * this robot support" for display/wire purposes must use this, not a bare `!= null`
 * check: a robot that sends `max_steer_angle: 0` (or another non-finite placeholder)
 * as an explicit "not applicable" sentinel still has to resolve to holonomic, the same
 * way resolveProfile's own `isPositiveFinite` already treats it.
 */
export function isAckermannLimits(limits: RobotLimits | null | undefined): boolean {
  const max = limits?.max_steer_angle;
  return max != null && Number.isFinite(max) && max > 0;
}

export interface CargoBoxCatalogEntry {
  id: string;
  label: string;
  /** Legacy ordering hint for the fixed cargo UI. Optional: a robot advertising its
   * cargo actions through the `commands` catalog (see command-catalog.ts) is ordered
   * by that catalog instead and doesn't need to send this. */
  uiSlot?: number;
}

/** `robot-config.robot` (PROTOCOL.md §4.3): what the planner reports about the robot. */
export interface RobotInfo {
  /** Robot model id, e.g. "so101". */
  model?: string;
  /** URDF served by this UI (e.g. "/robots/so101/so101_new_calib.urdf") for the 3D view. */
  description_url?: string;
  /** Joint names of `joint-state`, in order. */
  joints?: string[];
  /** Named goals the planner accepts (`planner-goal`), e.g. ["home"]. */
  goals?: string[];
  /** Backend ↔ planner link. */
  link?: "connected" | "disconnected";
  [key: string]: unknown;
}

export interface RobotConfigPayload {
  schema_version?: string;
  input_sources?: string[];
  units?: string;
  limits?: RobotLimits;
  start_velocity?: { vx?: number; vy?: number; vyaw?: number; steer_angle?: number };
  /**
   * Robot-declared command catalog (see command-catalog.ts) — which wire commands the
   * UI should actually offer, so a quadruped and a wheeled rover each get the controls
   * that make sense for them instead of one fixed control set. OPT-IN: omitting it (or
   * sending one that fails validation) just means no dynamic catalog — the UI falls
   * back to its legacy fixed posture/cargo controls, it does not fail robot-config.
   */
  commands?: RobotCommandEntry[];
  cameras?: CameraCatalogEntry[];
  cargo_boxes?: CargoBoxCatalogEntry[];
  robot?: RobotInfo;
}

export interface RobotConfigError {
  code?: string;
  detail?: string;
}

export interface RobotConfigSnapshot {
  status: RobotConfigStatus;
  config: RobotConfigPayload | null;
  error: RobotConfigError | null;
}

export const GET_CAPABILITIES_REQUEST = JSON.stringify({ event: "get-capabilities" });

export interface RobotConfigSlice extends Pick<
  StoreApi<RobotConfigSnapshot>,
  "getState" | "getInitialState" | "subscribe"
> {
  getLimits(): RobotLimits | null;
  getCameras(): CameraCatalogEntry[];
  getCargoBoxes(): CargoBoxCatalogEntry[];
  /** Resolved command catalog from the last successfully-ingested robot-config, or
   * null when that config didn't advertise one (or it failed validation) — see
   * RobotConfigPayload.commands's own doc comment. */
  getCommandCatalog(): CommandCatalog | null;
  reset(): void;
  ingest(message: unknown): void;
  /**
   * Force status to "error" if it's still "pending" — a hard ceiling on the whole
   * robot-config stage, independent of `scheduleCapabilitiesRequest`'s own retry
   * budget. Needed because that budget only helps if a request actually got SENT;
   * nothing here ever gets requested at all if its trigger (the operator's chat
   * DataChannel opening) itself fails — that path has no other way to time out.
   * No-op once status is "ready" or already "error" (idempotent, safe to call late).
   */
  timeout(detail?: string): void;
  scheduleCapabilitiesRequest(
    sendCommand: (text: string) => boolean | void,
    options?: { maxAttempts?: number; intervalMs?: number }
  ): () => void;
}

function parseMessage(message: unknown): unknown {
  let raw: unknown = message;
  if (typeof raw !== "string") {
    if (raw instanceof ArrayBuffer) raw = new TextDecoder().decode(new Uint8Array(raw));
    else if (ArrayBuffer.isView(raw)) raw = new TextDecoder().decode(raw);
    else raw = String(raw);
  }
  return JSON.parse(String(raw).trim());
}

export function normalizeCargoBoxesPayload(cargoBoxes: unknown): CargoBoxCatalogEntry[] {
  if (!Array.isArray(cargoBoxes)) return [];
  const out: CargoBoxCatalogEntry[] = [];
  const seenIds = new Set<string>();
  for (const item of cargoBoxes) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const id = String(rec.id || "").trim();
    const label = String(rec.label || id || "").trim();
    if (!id || !label) continue;
    const idKey = id.toLowerCase();
    if (seenIds.has(idKey)) continue;
    seenIds.add(idKey);
    const rawSlot = rec.uiSlot ?? rec.ui_slot;
    const uiSlot = Number(rawSlot);
    out.push({
      id,
      label,
      ...(Number.isFinite(uiSlot) && uiSlot >= 0 ? { uiSlot: Math.floor(uiSlot) } : {}),
    });
  }
  // Only sort by uiSlot when every box has one — a partial set (or none at all, the
  // catalog-driven case) keeps the robot's own array order instead of an arbitrary
  // mix of sorted-then-unsorted entries.
  if (out.length > 0 && out.every((box) => box.uiSlot != null)) {
    out.sort((a, b) => (a.uiSlot ?? 0) - (b.uiSlot ?? 0));
  }
  return out;
}

export function createRobotConfigSlice(): RobotConfigSlice {
  const store = createStore<RobotConfigSnapshot>(() => ({
    status: "pending",
    config: null,
    error: null,
  }));

  // Kept outside the zustand store on purpose: it's fully derived from `config` (see
  // ingest() below), not independent state a subscriber needs to react to on its own —
  // components read it straight off the resolved robotConfig (resolveCommandCatalog is
  // cheap and pure), this cache just saves recomputing it for getCommandCatalog()/tests.
  let commandCatalog: CommandCatalog | null = null;

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,

    getLimits: () => store.getState().config?.limits ?? null,
    getCameras: () => store.getState().config?.cameras ?? [],
    getCargoBoxes: () => store.getState().config?.cargo_boxes ?? [],
    getCommandCatalog: () => commandCatalog,

    reset() {
      commandCatalog = null;
      store.setState({ status: "pending", config: null, error: null }, true);
    },

    ingest(message) {
      try {
        const parsed = parseMessage(message) as Record<string, unknown>;
        const event = parsed.event ?? parsed.type;
        if (event === "robot-config") {
          const payload = parsed.payload ?? parsed.data;
          if (!payload || typeof payload !== "object") return;
          const rec = payload as Record<string, unknown>;
          const current = store.getState();
          if (current.status === "ready") {
            // Hot-plugged cameras and camera toggles update the live catalog. Other
            // configuration (limits/commands) retains its initial session values.
            if (current.config) {
              const robot =
                rec.robot && typeof rec.robot === "object"
                  ? (rec.robot as RobotInfo)
                  : current.config.robot;
              const inputSources = Array.isArray(rec.input_sources)
                ? rec.input_sources.filter((value): value is string => typeof value === "string")
                : current.config.input_sources;
              const cameras = Array.isArray(rec.cameras)
                ? normalizeCamerasPayload(rec.cameras)
                : current.config.cameras;
              const camerasChanged =
                JSON.stringify(cameras) !== JSON.stringify(current.config.cameras);
              if (
                JSON.stringify(robot) !== JSON.stringify(current.config.robot) ||
                JSON.stringify(inputSources) !== JSON.stringify(current.config.input_sources) ||
                camerasChanged
              ) {
                store.setState({
                  config: {
                    ...current.config,
                    robot,
                    input_sources: inputSources,
                    cameras: camerasChanged ? cameras : current.config.cameras,
                  },
                });
              }
            }
            return;
          }
          const camEntries = normalizeCamerasPayload(rec.cameras);
          const cargoBoxes = normalizeCargoBoxesPayload(rec.cargo_boxes);
          // `commands` is opt-in (see RobotConfigPayload.commands) — a robot that
          // omits it, or sends one that fails validation, still gets a "ready"
          // config; it just has no dynamic catalog, and the UI falls back to its
          // legacy fixed posture/cargo controls. Only `limits`/`cameras`/`cargo_boxes`
          // gate robot-config readiness itself.
          let catalog: CommandCatalog | null = null;
          if (rec.commands !== undefined) {
            const catalogResult = resolveCommandCatalog({
              limits:
                rec.limits && typeof rec.limits === "object"
                  ? (rec.limits as Record<string, unknown>)
                  : undefined,
              cargo_boxes: cargoBoxes,
              commands: rec.commands,
            });
            if (catalogResult.ok) catalog = catalogResult.catalog;
          }
          const config = {
            ...rec,
            ...(catalog ? { commands: catalog.commands } : {}),
            cameras: camEntries,
            cargo_boxes: cargoBoxes,
          } as RobotConfigPayload;
          commandCatalog = catalog;
          store.setState({ status: "ready", config, error: null }, true);
          return;
        }
        if (event === "capabilities-error") {
          if (store.getState().status === "ready") return;
          const payload = parsed.payload ?? parsed.data;
          const error =
            payload && typeof payload === "object"
              ? (payload as RobotConfigError)
              : { detail: "capabilities-error" };
          store.setState({ status: "error", config: store.getState().config, error }, true);
        }
      } catch {
        /* ignore unrelated telemetry */
      }
    },

    timeout(detail = "robot-config timeout") {
      if (store.getState().status !== "pending") return;
      store.setState(
        { status: "error", config: null, error: { code: "capabilities-timeout", detail } },
        true
      );
    },

    scheduleCapabilitiesRequest(sendCommand, options = {}) {
      const maxAttempts = options.maxAttempts ?? 20;
      const intervalMs = options.intervalMs ?? 2000;
      let attempt = 0;
      const timers: ReturnType<typeof setTimeout>[] = [];

      const isReady = (): boolean => store.getState().status === "ready";

      const trySend = (): void => {
        if (isReady()) return;
        attempt += 1;
        sendCommand(GET_CAPABILITIES_REQUEST);
        if (attempt < maxAttempts && !isReady()) {
          timers.push(setTimeout(trySend, intervalMs));
          return;
        }
        // Retries exhausted with no "robot-config" (nor an explicit
        // "capabilities-error") ever arriving — without this, status stays "pending"
        // forever with no further update, so a caller watching for "ready" has no
        // way to tell "still trying" from "gave up" (splash spins with no error/retry).
        // Still reversible: a late robot-config response after this still flips status
        // to "ready" normally (ingest() only special-cases the already-"ready" case).
        if (!isReady() && store.getState().status !== "error") {
          store.setState(
            {
              status: "error",
              config: store.getState().config,
              error: {
                code: "capabilities-timeout",
                detail: `нет ответа robot-config за ${attempt} попыток`,
              },
            },
            true
          );
        }
      };

      trySend();
      return () => {
        for (const id of timers) clearTimeout(id);
      };
    },
  };
}
